/**
 * Where the beta editor shows the party and property pickers: next to the
 * form fields they fill. Pure — no React.
 *
 * A party slot is placed right before the first visible field (in form
 * order) whose autofill binding reads that slot (`kind: 'party'`, same role
 * and ordinal). The property panel goes before the first visible
 * `property` / `property_list` field. A slot with no visible bound field —
 * it only signs, its fields are hidden by a condition, or the form has
 * none — stays in the compact panel at the top.
 */

import type { CompositionForm, FormState } from '@hyranai/regta-document-ir';

export type PlacementSlot = { role: string; ordinal?: number };

export type PanelPlacement = {
  /** Field key → indexes (into the given slots) of the party cards shown before it. */
  partiesBefore: ReadonlyMap<string, readonly number[]>;
  /** Field key the property panel is shown before, or null for the top. */
  propertyBefore: string | null;
  /** Indexes of the slots that stay in the top panel. */
  topSlots: readonly number[];
};

const ordinalOf = (o: number | undefined): number => o ?? 1;

export function placePanels(
  form: Pick<CompositionForm, 'groups'>,
  state: Pick<FormState, 'visibleKeys' | 'visibleGroups'>,
  slots: ReadonlyArray<PlacementSlot>,
  hasPropertyPanel: boolean,
): PanelPlacement {
  const visibleKeys = new Set(state.visibleKeys);
  const visibleGroups = new Set(state.visibleGroups);
  const partiesBefore = new Map<string, number[]>();
  const placed = new Set<number>();
  let propertyBefore: string | null = null;

  for (const group of form.groups) {
    if (!visibleGroups.has(group.instanceId)) continue;
    for (const field of group.fields) {
      if (!visibleKeys.has(field.key) || field.key.startsWith('__')) continue;
      const af = field.autofill;
      if (!af) continue;
      if (af.kind === 'party') {
        slots.forEach((slot, i) => {
          if (placed.has(i)) return;
          if (slot.role !== af.role || ordinalOf(slot.ordinal) !== ordinalOf(af.ordinal)) return;
          placed.add(i);
          const list = partiesBefore.get(field.key) ?? [];
          list.push(i);
          partiesBefore.set(field.key, list);
        });
      } else if (hasPropertyPanel && propertyBefore === null) {
        propertyBefore = field.key;
      }
    }
  }

  return {
    partiesBefore,
    propertyBefore,
    topSlots: slots.map((_, i) => i).filter((i) => !placed.has(i)),
  };
}
